/* eslint-disable complexity */
import { useCallback, useMemo, useState } from 'react';
import uniqBy from 'lodash/uniqBy';

import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { NBSP } from '@r1-frontend/ui-react/tokens/symbols';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { channelPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import ChannelListPopup from '~/src/components/Bundles/ChannelListPopup';
import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import SubscriptionsAdditionalInfoPopup from '~/src/features/full-buy/components/SubscriptionsPopup/SubscriptionsAdditionalInfoPopup';
import { selectSubscriptionPopup } from '~/src/features/full-buy/helpers/selectSubscriptionPopup';
import ElementInfo from '~/src/features/full-buy/SelectedTariffElements/ElementInfo';
import { IFullBuyTariff } from '~/src/features/full-buy/store/tariffTransformer';
import { TariffElement } from '~/src/features/full-buy/TariffElementNew';

import * as ST from './styled';

const CHANNELS_BLOCK_TEXT = 'Смотрите на любом устройстве в приложении Movix. Подключайте дополнительные пакеты каналов.';
type RequiredProducts = {
    displayImage: string,
    images: string[] | null,
    title: string,
    description: string,
    isOpen: boolean,
}
type Subscriptions = {
    displayImage: string,
    title: string,
    description: string[],
    isOpen: boolean,
}
interface IElementsInfo {
    requiredProducts: RequiredProducts,
    subscriptions: Subscriptions,
}

const initPopupData: IElementsInfo = {
    requiredProducts: {
        displayImage: '',
        images: [],
        title: '',
        description: '',
        isOpen: false,
    },
    subscriptions: {
        displayImage: '',
        title: '',
        description: [],
        isOpen: false,
    },
};

const getChannelsTitle = (channelCount: number | null, hdCount: number | null) => {
    let hdCountsTitle = '';
    if (hdCount) {
        hdCountsTitle = `(включая ${hdCount} HD ${channelText(hdCount)})`;
    }
    if (channelCount) {
        return `${channelCount} ${channelText(channelCount)}${NBSP}${hdCountsTitle}`;
    }

    return '';
};

interface IProps {
    tariffPackage: IFullBuyTariff,
}

const channelText = getPluralize(channelPluralize);

// TODO рефакторинг, пример компонент PurchaseItem (в ui-react)
export const SelectedTariffElements = ({
    tariffPackage,
}: IProps): JSX.Element => {
    const analytics = useAnalytics();

    const [isChannelsPopupOpen, setIsChannelsPopupOpen] = useState(false);
    const [selectedTariff, setSelectedTariff] = useState<IFullBuyTariff | null>(null);

    const {
        downloadSpeed,
        additionalProducts,
        subscriptions,
        channelCount,
        hdCount,
        alias,
    } = tariffPackage;
    const speed = tariffPackage.speed;

    const [elementsInfo, setElementInfo] = useState<IElementsInfo>(initPopupData);

    const requiredAdditionalProducts = useMemo(() => {
        const filteredProducts = additionalProducts?.filter(item => item.required);
        return uniqBy(filteredProducts, 'id');
    }, [additionalProducts]);

    const sortedOfferContents = useMemo(() => [...subscriptions].sort((a, b) => {
        if (a.sort === null) {
            return 1;
        }
        if (b.sort === null) {
            return -1;
        }
        return a.sort - b.sort;
    }), [subscriptions]);

    const onProductButtonClickHandler = useCallback((id?: number) => {
        const shopItem = requiredAdditionalProducts.find(item => item.id === id);

        setElementInfo({
            ...elementsInfo,
            requiredProducts: {
                displayImage: shopItem?.image || '',
                description: shopItem?.description  || '',
                title: shopItem?.title  || '',
                images: shopItem?.images || null,
                isOpen: true,
            },
        });

        analytics.tariff.popupOpened(shopItem?.title);
    }, [requiredAdditionalProducts]);

    const onOfferButtonClickHandler = useCallback((id?: number) => {
        const offer = subscriptions.find(item => item.id === id);

        setElementInfo({
            ...elementsInfo,
            subscriptions: offer ? selectSubscriptionPopup(offer) : initPopupData.subscriptions,
        });

        analytics.tariff.popupOpened(offer?.title);
    }, [subscriptions]);

    const channelsPopupOpen = useCallback((tariff) => {
        setSelectedTariff(tariff);
        setIsChannelsPopupOpen(true);

        analytics.tariff.popupOpened('tv_channels');
    }, []);

    const channelsPopupClose = useCallback(() => {
        setIsChannelsPopupOpen(false);
        setSelectedTariff(null);
    }, []);

    const isShowChannel = !!channelCount;

    const hasTariffElements = !!requiredAdditionalProducts?.length || !!subscriptions?.length || isShowChannel;

    const isMega = alias?.toString().includes('mega');

    const closePopup = useCallback(() => {
        setElementInfo(initPopupData);
    }, []);

    return (
        <ST.SelectedTariffElements>
            {/* Выбранный тариф */}
            <TariffElement
                title={`Интернет ${speed} Мбит/с`}
                titleText={downloadSpeed}
                imageProps={{
                    textForImage: `${speed}`,
                    bgForImage: tariffPackage.image_background || '',
                    isMega: isMega,
                }}
            />

            {hasTariffElements &&
                <ST.IncludedTariffElements>
                    {/* Каналы */}
                    {isShowChannel &&
                        <TariffElement
                            key={'channels'}
                            title={getChannelsTitle(channelCount, hdCount)}
                            titleText={CHANNELS_BLOCK_TEXT}
                            imageProps={{
                                imageSrc: '/static/images/full-buy/channels_preview.png',
                                channelCount: channelCount ? channelCount - 3 : undefined,
                                textForImage: `${speed}`,
                                imgBgColor: COLORS.BgSurface,
                            }}
                            btnProps={{
                                btnTextDesktop: 'Показать все каналы',
                                iconNameMobile: 'arrow-right',
                                onButtonClick: () => channelsPopupOpen(tariffPackage),
                            }}
                        />
                    }

                    {/* Оборудование */}
                    {requiredAdditionalProducts?.map((requiredProduct) => {
                        const isShowButton = requiredProduct.image && requiredProduct.description;

                        return (
                            <TariffElement
                                title={requiredProduct.title}
                                titleText={requiredProduct.body}
                                imageProps={{
                                    imageSrc: `${requiredProduct.image}`,
                                    textForImage: `${speed}`,
                                }}
                                btnProps={{
                                    btnTextDesktop: 'Подробнее',
                                    iconNameMobile: 'arrow-right',
                                    onButtonClick: isShowButton ? () => onProductButtonClickHandler(requiredProduct.id) : null,
                                }}
                                key={requiredProduct.id}
                            />
                        );
                    })}

                    {/* Подписки */}
                    {sortedOfferContents?.map((item) => {
                        const isShowButton = !!item.imageForFullBuyWebp && !!item.bodyForFullBuy;

                        return (
                            <TariffElement
                                key={item.id}
                                title={item.title || ''}
                                titleText={item.bodyForFullBuy || ''}
                                imageProps={{
                                    imageSrc: item.image_mobile,
                                    textForImage: `${speed}`,
                                }}
                                btnProps={{
                                    btnTextDesktop: 'Подробнее',
                                    iconNameMobile: 'arrow-right',
                                    onButtonClick: isShowButton ? () => onOfferButtonClickHandler(item.id) : null,
                                }}
                            />
                        );
                    })}
                </ST.IncludedTariffElements>
            }

            <ChannelListPopup
                isOpen={isChannelsPopupOpen}
                onClose={channelsPopupClose}
                freePackets={selectedTariff?.freePackets}
            />
            <ElementInfo
                onClose={closePopup}
                {...elementsInfo.requiredProducts}
            />

            <SubscriptionsAdditionalInfoPopup
                onClose={closePopup}
                {...elementsInfo.subscriptions}
            />
        </ST.SelectedTariffElements>
    );
};
